// local/modules/dzen.chat/install/js/src/chat/components/VerificationManager.js

// Глобальные переменные для хранения состояния верификации
if (typeof window.userVerified === 'undefined') {
    window.userVerified = false;
}
if (typeof window.userId === 'undefined') {
    window.userId = 0;
}
if (typeof window.userName === 'undefined') {
    window.userName = '';
}
if (typeof window.userLastName === 'undefined') {
    window.userLastName = '';
}
if (typeof window.userPhone === 'undefined') {
    window.userPhone = '';
}
if (typeof window.userEmail === 'undefined') {
    window.userEmail = '';
}

export const VerificationManager = {
    props: {
        isAuth: {
            type: Boolean,
            default: false,
        },
        initialPhone: {
            type: String,
            default: '',
        },
        initialEmail: {
            type: String,
            default: '',
        },
        hideEmail: {
            type: Boolean,
            default: false,
        },
        showNameField: {
            type: Boolean,
            default: true,
        },
        showLastNameField: {
            type: Boolean,
            default: true,
        },
        showQuestionField: {
            type: Boolean,
            default: false,
        },
        initialQuestion: {
            type: String,
            default: '',
        },
        headerText: {
            type: String,
            default: 'Вход в чат',
        },
        submitCallback: {
            type: Function,
            default: null,
        },
        targetUserId: {
            type: [String, Number],
            default: null,
        },
        compactMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            // Шаг процесса: 'contact' -> 'code' -> 'verified'
            step: 'contact',

            // Режим авторизации: 'phone' или 'email'
            authMode: 'phone',

            checkUserTimeout: null,

            // Личные данные пользователя
            firstName: window.userName || '',
            lastName: window.userLastName || '',
            phone: this.initialPhone || window.userPhone || '',
            email: this.initialEmail || window.userEmail || '',
            displayedPhone: this.formatPhone(this.initialPhone || window.userPhone || ''),
            question: this.initialQuestion || '',

            // Валидация полей
            firstNameError: '',
            lastNameError: '',
            phoneError: '',
            emailError: '',
            codeError: '',
            questionError: '',

            // Состояния валидации
            isFirstNameValid: false,
            isLastNameValid: false,
            isPhoneValid: false,
            isEmailValid: false,
            isCodeValid: false,
            areNameFieldsValid: false,

            // Процесс отправки кода
            authCode: '',
            codePlaceholder: '••••',
            isSendingCode: false,
            isCodeSent: false,
            isCodeVerified: false,

            // Таймеры и блокировки
            resendTimeout: 0,
            attemptNumber: 1,
            maxAttempts: 3,
            isBlocked: false,
            blockTimeRemaining: 0,
            lastRequestId: null,
            timers: {},
            serverBlockTime: 0,

            // Блокировка полей после подтверждения
            isContactFieldLocked: false,
            isCodeFieldLocked: false,

            // Проверка существования пользователя
            userExists: null,
            checkingUser: false,
            userCheckError: '',

            // Соглашения
            serverConsents: [],
            consents: {},
            showAgreementError: false,
            showConsentsBlock: false,

            // Маска телефона
            phoneInput: null,
            maskInitialized: false,
        }
    },
    computed: {
        // Форматированное время для таймера
        countdownFormatted() {
            const mins = Math.floor(this.resendTimeout / 60)
            const secs = this.resendTimeout % 60
            return `${mins}:${secs < 10 ? '0' : ''}${secs}`
        },

        // Доступность кнопки отправки кода
        canSendCode() {
            if (this.authMode === 'phone') {
                return this.isPhoneValid &&
                    this.areNameFieldsValid &&
                    !this.isSendingCode &&
                    !this.isBlocked &&
                    !this.isCodeSent &&
                    !this.isCodeVerified;
            } else {
                return this.isEmailValid &&
                    this.areNameFieldsValid &&
                    !this.isSendingCode &&
                    !this.isBlocked &&
                    !this.isCodeSent &&
                    !this.isCodeVerified;
            }
        },

        // Доступность кнопки подтверждения кода
        canVerifyCode() {
            return this.authCode.length === 4 &&
                !this.isSendingCode &&
                !this.isBlocked &&
                this.isCodeSent &&
                !this.isCodeVerified;
        },

        // Доступность кнопки повторной отправки
        canResendCode() {
            return this.resendTimeout <= 0 &&
                !this.isSendingCode &&
                !this.isBlocked &&
                this.isCodeSent &&
                !this.isCodeVerified;
        },

        // Проверка всех обязательных соглашений
        allRequiredAgreementsAccepted() {
            return this.serverConsents
                .filter(consent => consent.required)
                .every(consent => this.consents[consent.key])
        },

        // Показывать блок соглашений
        shouldShowConsentsBlock() {
            return (this.authMode === 'phone' ? this.isPhoneValid : this.isEmailValid) &&
                this.userExists === false &&
                this.serverConsents.length > 0;
        },

        // Доступность кнопки отправки вопроса (если включено)
        canSubmitQuestion() {
            if (!this.showQuestionField) return false;
            return this.isCodeVerified &&
                this.question.trim().length > 0 &&
                !this.isSendingCode;
        },

        // Доступность полной формы
        isFormComplete() {
            const baseValid = this.isCodeVerified && this.areNameFieldsValid;

            if (this.showQuestionField) {
                return baseValid && this.question.trim().length > 0;
            }

            return baseValid;
        },
    },
    watch: {
        // Автоматическая проверка существования пользователя при изменении контакта
        phone(newVal) {
            if (this.authMode === 'phone' && newVal && newVal.length === 11) {
                // Автоматически форматируем если нет displayedPhone
                if (!this.displayedPhone || this.displayedPhone.replace(/\D/g, '').length !== 11) {
                    this.displayedPhone = this.formatPhone(newVal);
                }
                this.validatePhone();
                if (this.areNameFieldsValid) {
                    this.checkUserExists();
                }
            }
        },
        email(newVal) {
            if (this.authMode === 'email' && this.isEmailValid) {
                this.checkUserExists();
            }
        },
        authMode(newVal) {
            // При переключении режима сбрасываем состояние
            this.resetContactState();

            if (newVal === 'phone') {
                this.$nextTick(() => {
                    this.initPhoneMask();
                });
            }
        },
        firstName(newVal) {
            this.validateFirstName();
        },
        lastName(newVal) {
            this.validateLastName();
        },
        authCode(newVal) {
            this.validateCode();

            // Автоматическая отправка при вводе 4 цифр
            if (newVal.length === 4 && /^\d{4}$/.test(newVal)) {
                if (this.canVerifyCode) {
                    this.verifyCode();
                }
            }
        },
        displayedPhone(newVal) {
            // Только для отладки - можно удалить позже
            console.log('displayedPhone changed to:', newVal);

            if (this.authMode === 'phone' && newVal) {
                // Обновляем phone на основе отформатированного значения
                const digits = newVal.replace(/\D/g, '');
                if (digits.length === 11) {
                    this.phone = digits;
                }
            }
        },
    },
    methods: {
        // ============ ВАЛИДАЦИЯ ПОЛЕЙ ============
        validateFirstName() {
            this.firstNameError = this.firstName.trim().length >= 2
                ? ''
                : 'Имя должно содержать минимум 2 символа';
            this.isFirstNameValid = !this.firstNameError;
            this.updateNameFieldsValidity();
        },

        validateLastName() {
            this.lastNameError = this.lastName.trim().length >= 2
                ? ''
                : 'Фамилия должна содержать минимум 2 символа';
            this.isLastNameValid = !this.lastNameError;
            this.updateNameFieldsValidity();
        },

        updateNameFieldsValidity() {
            this.areNameFieldsValid = this.isFirstNameValid && this.isLastNameValid;
        },

        checkFormValidity() {
            // Проверяем имя и фамилию
            this.validateFirstName();
            this.validateLastName();

            // Проверяем контакт в зависимости от режима
            if (this.authMode === 'phone') {
                this.validatePhone();
            } else {
                this.validateEmail();
            }

            this.updateNameFieldsValidity();
        },

        validatePhone() {
            const digitsOnly = this.phone ? this.phone.replace(/\D/g, '') : '';
            this.isPhoneValid = digitsOnly.length === 11;
            this.phoneError = this.isPhoneValid
                ? ''
                : 'Номер телефона должен содержать 11 цифр';
        },

        validateEmail() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.isEmailValid = emailRegex.test(this.email);
            this.emailError = this.isEmailValid
                ? ''
                : 'Введите корректный email адрес';
        },

        validateCode() {
            this.authCode = this.authCode.replace(/\D/g, '');
            if (this.authCode.length > 4) {
                this.authCode = this.authCode.slice(0, 4);
            }
            this.isCodeValid = /^\d{0,4}$/.test(this.authCode);
        },

        validateQuestion() {
            this.questionError = this.question.trim().length > 0
                ? ''
                : 'Введите ваш вопрос';
        },

        // ============ ФОРМАТИРОВАНИЕ ============
        formatPhone(phone) {
            if (!phone) return '';
            const cleaned = phone.toString().replace(/\D/g, '');

            // Если номер начинается с 8, заменяем на 7
            let digits = cleaned;
            if (digits.startsWith('8')) {
                digits = '7' + digits.substring(1);
            }
            // Если номер начинается не с 7, добавляем 7
            else if (!digits.startsWith('7')) {
                digits = '7' + digits;
            }

            if (digits.length === 11) {
                return `+7(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 9)}-${digits.substring(9, 11)}`;
            }
            return `+7${digits.substring(1)}`; // Возвращаем что есть
        },

        // ============ МАСКА ТЕЛЕФОНА ============
        formatPhoneInput(event) {
            if (!event || !event.target) return;

            const input = event.target;
            let value = input.value;
            const cursorPosition = input.selectionStart;

            // Сохраняем только цифры и плюс
            let digits = value.replace(/[^\d+]/g, '');

            // Автоматически добавляем +7 если нужно
            if (!digits.startsWith('+')) {
                digits = '+' + digits;
            }
            if (!digits.startsWith('+7')) {
                digits = '+7' + digits.substring(1); // заменяем первый символ после + на 7
            }

            // Ограничиваем количество цифр (10 цифр после +7)
            const phoneDigits = digits.substring(2); // цифры после +7
            if (phoneDigits.length > 10) {
                digits = '+7' + phoneDigits.substring(0, 10);
            }

            // Форматируем номер по маске +7(XXX)XXX-XX-XX
            let formatted = '+7';
            if (phoneDigits.length > 0) {
                formatted += '(' + phoneDigits.substring(0, 3);
            }
            if (phoneDigits.length > 3) {
                formatted += ')' + phoneDigits.substring(3, 6);
            }
            if (phoneDigits.length > 6) {
                formatted += '-' + phoneDigits.substring(6, 8);
            }
            if (phoneDigits.length > 8) {
                formatted += '-' + phoneDigits.substring(8, 10);
            }

            // Обновляем значения
            input.value = formatted;
            this.displayedPhone = formatted;
            this.phone = '+7' + phoneDigits; // сохраняем для отправки на сервер

            // РАССЧИТЫВАЕМ НОВУЮ ПОЗИЦИЮ КУРСОРА
            let newCursorPosition = this.calculateNewCursorPosition(
                cursorPosition,
                value,
                formatted,
                event.inputType
            );

            // Устанавливаем новую позицию курсора
            this.$nextTick(() => {
                input.setSelectionRange(newCursorPosition, newCursorPosition);
            });

            // Валидируем
            this.validatePhone();
        },

        calculateNewCursorPosition(oldPos, oldValue, newValue, inputType) {
            // Если это вставка (paste) или другие специальные операции
            if (inputType === 'insertFromPaste' ||
                inputType === 'deleteContentBackward' ||
                inputType === 'deleteContentForward') {

                // После вставки ставим курсор в конец
                if (inputType === 'insertFromPaste') {
                    return newValue.length;
                }

                // После удаления пытаемся сохранить позицию
                const oldDigitCount = this.countDigitsBeforePosition(oldValue, oldPos);
                return this.findCursorPositionByDigitCount(newValue, oldDigitCount);
            }

            // Для обычного ввода цифры
            const oldDigitCount = this.countDigitsBeforePosition(oldValue, oldPos);

            // Если пользователь вводил цифру, двигаем курсор вперед
            if (oldPos > 0) {
                const charBeforeCursor = oldValue.charAt(oldPos - 1);
                if (/\d/.test(charBeforeCursor)) {
                    // Ищем позицию этой же цифры в новом значении
                    return this.findCursorPositionByDigitCount(newValue, oldDigitCount + 1);
                }
            }

            // По умолчанию ставим курсор после последнего символа
            return this.findCursorPositionByDigitCount(newValue, oldDigitCount);
        },

        countDigitsBeforePosition(value, position) {
            let count = 0;
            for (let i = 0; i < Math.min(position, value.length); i++) {
                if (/\d/.test(value.charAt(i))) {
                    count++;
                }
            }
            return count;
        },


        // Вспомогательный метод для поиска позиции цифры
        findDigitPosition(formatted, targetPosition) {
            let digitCount = 0;
            for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted.charAt(i))) {
                    digitCount++;
                    if (digitCount === targetPosition) {
                        return i;
                    }
                }
            }
            return -1;
        },

        findCursorPositionByDigitCount(value, digitCount) {
            let count = 0;
            for (let i = 0; i < value.length; i++) {
                if (/\d/.test(value.charAt(i))) {
                    count++;
                }
                if (count === digitCount) {
                    // Ставим курсор после этой цифры, но учитываем форматирующие символы
                    return i + 1;
                }
            }
            // Если не нашли, ставим в конец
            return value.length;
        },

        // Метод для форматирования телефона при потере фокуса
        formatPhoneOnBlur() {
            // Если поле пустое или содержит только +7
            if (!this.displayedPhone || this.displayedPhone === '+7' || this.displayedPhone === '+7(') {
                this.displayedPhone = '';
                this.phone = '';
                this.isPhoneValid = false;
                return;
            }

            // Если номер неполный, очищаем
            const digits = this.displayedPhone.replace(/\D/g, '');
            if (digits.length !== 11) {
                this.displayedPhone = '';
                this.phone = '';
                this.isPhoneValid = false;
                return;
            }

            this.validatePhone();
        },

        handlePhoneKeydown(event) {
            const input = event.target;
            const cursorPosition = input.selectionStart;
            const selectionLength = input.selectionEnd - cursorPosition;

            // Обработка Backspace
            if (event.key === 'Backspace') {
                // Если выделен текст - удаляем выделение
                if (selectionLength > 0) {
                    return; // Позволяем стандартное поведение
                }

                // Если курсор сразу после форматирующего символа
                const charBeforeCursor = input.value.charAt(cursorPosition - 1);
                if (!/\d/.test(charBeforeCursor) && charBeforeCursor !== '+') {
                    event.preventDefault();
                    // Перемещаем курсор назад и снова вызываем Backspace
                    input.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
                    // Имитируем Backspace
                    const fakeEvent = new KeyboardEvent('keydown', {
                        key: 'Backspace',
                        bubbles: true
                    });
                    input.dispatchEvent(fakeEvent);
                    return;
                }

                // Не позволяем удалить +7
                if (cursorPosition <= 2) {
                    event.preventDefault();
                    return;
                }
            }

            // Обработка Delete
            if (event.key === 'Delete') {
                // Если выделен текст - удаляем выделение
                if (selectionLength > 0) {
                    return; // Позволяем стандартное поведение
                }

                // Если курсор перед форматирующим символом
                const charAtCursor = input.value.charAt(cursorPosition);
                if (!/\d/.test(charAtCursor) && charAtCursor !== '+') {
                    event.preventDefault();
                    // Перемещаем курсор вперед и снова вызываем Delete
                    input.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
                    // Имитируем Delete
                    const fakeEvent = new KeyboardEvent('keydown', {
                        key: 'Delete',
                        bubbles: true
                    });
                    input.dispatchEvent(fakeEvent);
                    return;
                }
            }

            // Обработка цифровых клавиш
            if (/\d/.test(event.key)) {
                // Не позволяем вводить цифры до +7
                if (cursorPosition < 2) {
                    event.preventDefault();
                    // Перемещаем курсор после +7
                    input.setSelectionRange(2, 2);
                    // Имитируем ввод цифры на правильной позиции
                    const fakeEvent = new InputEvent('input', {
                        inputType: 'insertText',
                        data: event.key,
                        bubbles: true
                    });
                    input.value = input.value.substring(0, 2) + event.key + input.value.substring(2);
                    input.dispatchEvent(fakeEvent);

                }
            }
        },

        // ============ ТАЙМЕРЫ ============
        startTimer(name, interval, callback) {
            this.clearTimer(name);
            this.timers[name] = setInterval(callback, interval);
        },

        clearTimer(name) {
            if (this.timers[name]) {
                clearInterval(this.timers[name]);
                delete this.timers[name];
            }
        },

        clearAllTimers() {
            Object.keys(this.timers).forEach(name => this.clearTimer(name));
        },

        startResendTimer(seconds = 45) {
            this.resendTimeout = seconds;
            this.startTimer('resend', 1000, () => {
                this.resendTimeout--;
                if (this.resendTimeout <= 0) {
                    this.clearTimer('resend');
                }
            });
        },

        // ============ БЛОКИРОВКИ ============
        blockUser(seconds = 900) {
            this.isBlocked = true;
            this.blockTimeRemaining = seconds;

            // Сохраняем в localStorage
            const blockedUntil = Date.now() + seconds * 1000;
            localStorage.setItem('chatAuthBlockedUntil', blockedUntil);

            this.startTimer('block', 1000, () => {
                this.blockTimeRemaining--;
                if (this.blockTimeRemaining <= 0) {
                    this.resetBlockStatus();
                }
            });
        },

        checkBlockStatus() {
            const blockedUntil = localStorage.getItem('chatAuthBlockedUntil');
            if (!blockedUntil) return;

            const remaining = Math.round((blockedUntil - Date.now()) / 1000);
            if (remaining > 0) {
                this.blockUser(remaining);
            } else {
                this.resetBlockStatus();
            }
        },

        resetBlockStatus() {
            this.clearTimer('block');
            this.isBlocked = false;
            this.attemptNumber = 1;
            this.blockTimeRemaining = 0;
            localStorage.removeItem('chatAuthBlockedUntil');
        },

        // ============ API ЗАПРОСЫ ============
        async sendCodeToServer(type, data) {
            return new Promise((resolve) => {
                BX.ajax({
                    url: '/api/v1/auth/sendCode/',
                    method: 'POST',
                    data: {...data, type, sessid: BX.bitrix_sessid()},
                    onsuccess: (result) => {
                        try {
                            resolve(JSON.parse(result));
                        } catch {
                            resolve({status: 'error', message: 'Ошибка сервера'});
                        }
                    },
                    onfailure: () => {
                        resolve({status: 'error', message: 'Ошибка соединения'});
                    },
                });
            });
        },

        async verifyCodeWithServer(type, data) {
            return new Promise((resolve) => {
                BX.ajax({
                    url: '/api/v1/auth/checkCode/',
                    method: 'POST',
                    data: {...data, type, sessid: BX.bitrix_sessid()},
                    onsuccess: (result) => {
                        try {
                            resolve(JSON.parse(result));
                        } catch {
                            resolve({status: 'error', message: 'Ошибка сервера'});
                        }
                    },
                    onfailure: () => {
                        resolve({status: 'error', message: 'Ошибка соединения'});
                    },
                });
            });
        },

        async checkUserOnServer(type, data) {
            return new Promise((resolve) => {
                BX.ajax({
                    url: '/api/v1/auth/checkUser/',
                    method: 'POST',
                    data: {...data, type, sessid: BX.bitrix_sessid()},
                    onsuccess: (result) => {
                        try {
                            resolve(JSON.parse(result));
                        } catch {
                            resolve({exists: false, consents: null});
                        }
                    },
                    onfailure: () => {
                        resolve({exists: false, consents: null});
                    },
                });
            });
        },

        removePhoneMask() {
            const input = this.$el.querySelector('.auth-input[type="tel"]')
            if (input) {
                $(input).unmask()
                input.value = ''
            }
        },

        initPhoneMask() {

            if (this.phoneInput && this.maskInitialized) {
                $(this.phoneInput).unmask()
            }

            this.phoneInput = this.$el.querySelector('#phone-input')

            if (!this.phoneInput) return

            $(this.phoneInput).mask('+7(999) 999-99-99', {
                completed: () => {
                    this.phone = this.phoneInput.value.replace(/\D/g, '')
                    this.validatePhone()
                },
                onChange: () => {
                    this.displayedPhone = this.phoneInput.value
                },
            })

            if (this.phone) {
                this.phoneInput.value = this.formatPhone(this.phone)
            }

            this.maskInitialized = true
        },

        // ============ ОСНОВНЫЕ МЕТОДЫ ============
        toggleAuthMode() {
            // Сохраняем текущие значения перед переключением
            const currentPhone = this.phone;
            const currentDisplayedPhone = this.displayedPhone || this.formatPhone(this.phone);
            const currentEmail = this.email;

            // Удаляем маску телефона перед переключением
            this.removePhoneMask();

            // Переключаем режим
            this.authMode = this.authMode === 'phone' ? 'email' : 'phone';

            // Сбрасываем только ошибки и валидацию, но сохраняем значения
            this.phoneError = '';
            this.emailError = '';
            this.isPhoneValid = false;
            this.isEmailValid = false;
            this.isCodeSent = false;
            this.authCode = '';
            this.codeError = '';
            this.userExists = null;
            this.serverConsents = [];
            this.consents = {};
            this.showAgreementError = false;

            // Восстанавливаем значения в зависимости от нового режима
            if (this.authMode === 'phone') {
                this.phone = currentPhone;
                this.displayedPhone = currentDisplayedPhone;
                this.email = ''; // Очищаем email при переключении на телефон
            } else {
                this.email = currentEmail;
                this.phone = ''; // Очищаем телефон при переключении на email
                this.displayedPhone = '';
            }

            // Сбрасываем шаг только если не было отправки кода
            if (!this.isCodeSent) {
                this.step = 'contact';
            }

            // Если возвращаемся к телефону - инициализируем маску с сохраненным значением
            if (this.authMode === 'phone') {
                this.$nextTick(() => {
                    this.initPhoneMask();
                });
            }
        },

        formatPhoneOnInput(event) {
            if (!event || !event.target) return;

            const input = event.target;
            let value = input.value.replace(/\D/g, '');

            // Ограничиваем длину
            if (value.length > 11) {
                value = value.substring(0, 11);
            }

            // Форматируем только если есть цифры
            if (value.length > 0) {
                // Форматируем как +7(XXX) XXX-XX-XX
                let formatted = '+7';
                if (value.length > 1) {
                    formatted += '(' + value.substring(1, 4);
                }
                if (value.length > 4) {
                    formatted += ') ' + value.substring(4, 7);
                }
                if (value.length > 7) {
                    formatted += '-' + value.substring(7, 9);
                }
                if (value.length > 9) {
                    formatted += '-' + value.substring(9, 11);
                }

                input.value = formatted;
                this.displayedPhone = formatted;
                this.phone = value;
            } else {
                input.value = '';
                this.displayedPhone = '';
                this.phone = '';
            }

            this.validatePhone();
        },

        resetContactState() {
            this.phone = '';
            this.displayedPhone = '';
            this.email = '';
            this.phoneError = '';
            this.emailError = '';
            this.isPhoneValid = false;
            this.isEmailValid = false;
            this.isCodeSent = false;
            this.authCode = '';
            this.codeError = '';
            this.userExists = null;
            this.serverConsents = [];
            this.consents = {};
            this.showAgreementError = false;
            this.step = 'contact';
        },

        async checkUserExists() {
            if (this.checkingUser) return;

            // Debounce - ждем 500ms после последнего ввода
            if (this.checkUserTimeout) {
                clearTimeout(this.checkUserTimeout);
            }

            this.checkUserTimeout = setTimeout(async () => {
                this.checkingUser = true;
                this.userCheckError = '';

                try {
                    const type = this.authMode === 'phone' ? 'phone' : 'email';
                    const data = this.authMode === 'phone'
                        ? {phone: this.phone}
                        : {email: this.email};

                    const response = await this.checkUserOnServer(type, data);
                    const result = response.data || {};

                    this.userExists = result.exists;

                    if (!this.userExists && result.consents) {
                        this.serverConsents = result.consents;
                        // Инициализируем объект consents
                        this.consents = this.serverConsents.reduce((acc, consent) => {
                            acc[consent.key] = false;
                            return acc;
                        }, {});
                    } else {
                        this.serverConsents = [];
                        this.consents = {};
                    }

                    // Если пользователь существует, можно сразу отправлять код
                    if (this.userExists === true && this.areNameFieldsValid) {
                        // Автоматически активируем отправку кода
                        this.$nextTick(() => {
                            // Проверяем, что все поля заполнены корректно
                            if (this.canSendCode) {
                                // Можно добавить автоматическую отправку или просто активировать кнопку
                                console.log('User exists, ready to send code');
                            }
                        });
                    }
                } catch (error) {
                    console.error('Ошибка проверки пользователя:', error);
                    this.userCheckError = 'Ошибка при проверке пользователя';
                } finally {
                    this.checkingUser = false;
                }
            }, 500);
        },

        async sendVerificationCode() {
            if (!this.canSendCode) return;

            // Проверяем соглашения для новых пользователей
            if (this.userExists === false && !this.allRequiredAgreementsAccepted) {
                this.showAgreementError = true;
                this.$nextTick(() => {
                    const errorElement = this.$el.querySelector('.consents-block');
                    if (errorElement) {
                        errorElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                    }
                });
                return;
            }

            this.isSendingCode = true;
            this.codeError = '';
            this.showAgreementError = false;

            try {
                const type = this.authMode === 'phone' ? 'phone' : 'email';
                const requestData = {
                    ...(this.authMode === 'phone' ? {phone: this.phone} : {email: this.email}),
                    userName: this.firstName.trim(),
                    userLastName: this.lastName.trim(),
                    ...(this.userExists === false && {
                        consents: Object.keys(this.consents).reduce((acc, key) => {
                            acc[key] = this.consents[key];
                            return acc;
                        }, {}),
                    }),
                };

                const response = await this.sendCodeToServer(type, requestData);
                const responseData = response.data || {};

                if (responseData.attemptNumber && parseInt(responseData.attemptNumber) > 0) {
                    this.attemptNumber = parseInt(responseData.attemptNumber) + 1;
                }

                // Проверка на блокировку сервера
                if (responseData.isBlocked) {
                    this.serverBlockTime = parseInt(responseData.isBlocked) || 900;
                    this.blockUser(this.serverBlockTime);

                    this.clearTimer('serverBlock');
                    this.startTimer('serverBlock', 1000, () => {
                        this.serverBlockTime--;
                        if (this.serverBlockTime <= 0) {
                            this.clearTimer('serverBlock');
                            this.serverBlockTime = 0;
                        }
                    });

                    throw new Error(`Превышено количество попыток. Попробуйте через ${this.countdownFormatted}`);
                }

                if (response.status === 'success') {
                    this.lastRequestId = responseData.request_id;
                    this.isCodeSent = true;
                    this.step = 'code';
                    this.startResendTimer(60);
                    this.attemptNumber = 1;

                    // Блокируем поле контакта
                    this.isContactFieldLocked = true;
                } else {
                    throw new Error(response.message || 'Ошибка при отправке кода');
                }
            } catch (error) {
                console.error('Ошибка отправки кода:', error);
                this.codeError = error.message;
            } finally {
                this.isSendingCode = false;
            }
        },

        async verifyCode() {
            if (!this.canVerifyCode) return;

            this.isSendingCode = true;
            this.codeError = '';

            try {
                const type = this.authMode === 'phone' ? 'phone' : 'email';
                const data = {
                    code: this.authCode,
                    attemptNumber: this.attemptNumber,
                    request_id: this.lastRequestId,
                    ...(this.authMode === 'phone' ? {phone: this.phone} : {email: this.email}),
                };

                const response = await this.verifyCodeWithServer(type, data);

                if (response.status === 'success') {
                    this.isCodeVerified = true;
                    this.step = 'verified';

                    // Обновляем глобальные переменные
                    if (response.data && response.data.user_id) {
                        window.userVerified = true;
                        window.userId = response.data.user_id;
                        window.userName = this.firstName.trim();
                        window.userLastName = this.lastName.trim();
                        window.userPhone = this.authMode === 'phone' ? this.phone : '';
                        window.userEmail = this.authMode === 'email' ? this.email : '';

                        console.debug('User verified with ID:', window.userId);
                    }

                    // Отправляем событие о завершении верификации
                    const verificationEvent = new CustomEvent('chatVerificationComplete', {
                        detail: {
                            verified: true,
                            userId: window.userId,
                            firstName: this.firstName.trim(),
                            lastName: this.lastName.trim(),
                            phone: this.authMode === 'phone' ? this.phone : '',
                            email: this.authMode === 'email' ? this.email : '',
                            name: `${this.firstName.trim()} ${this.lastName.trim()}`.trim(),
                        }
                    });
                    document.dispatchEvent(verificationEvent);

                    // Отправляем событие Vue
                    this.$emit('verification-complete', {
                        verified: true,
                        userId: window.userId,
                        firstName: this.firstName.trim(),
                        lastName: this.lastName.trim(),
                        phone: this.authMode === 'phone' ? this.phone : '',
                        email: this.authMode === 'email' ? this.email : '',
                        name: `${this.firstName.trim()} ${this.lastName.trim()}`.trim(),
                    });

                    // Блокируем все поля
                    this.isContactFieldLocked = true;
                    this.isCodeFieldLocked = true;

                    // Очищаем таймеры
                    this.clearAllTimers();
                    this.resendTimeout = 0;
                } else {
                    throw new Error(response.message || 'Неверный код подтверждения');
                }
            } catch (error) {
                console.error('Ошибка проверки кода:', error);
                this.codeError = error.message;
                this.authCode = '';

                this.attemptNumber++;
                if (error.message.includes('блокировка') || this.attemptNumber >= this.maxAttempts) {
                    this.blockUser();
                }
            } finally {
                this.isSendingCode = false;
            }
        },

        async resendVerificationCode() {
            if (!this.canResendCode) return;

            this.attemptNumber++;
            await this.sendVerificationCode();
        },

        async submitQuestion() {
            if (!this.canSubmitQuestion) return;

            this.isSendingCode = true;

            try {
                // Если указан callback, вызываем его
                if (typeof this.submitCallback === 'function') {
                    this.submitCallback(
                        this.question,
                        this.firstName.trim(),
                        this.lastName.trim(),
                        this.authMode === 'phone' ? this.phone : this.email,
                        window.userId
                    );
                } else if (this.targetUserId) {
                    // Или отправляем сообщение в чат
                    const messageData = {
                        RECIPIENT_ID: this.targetUserId,
                        MESSAGE: this.question,
                    };

                    const response = await this.sendMessageToChat(messageData);

                    if (response.status !== 'success') {
                        throw new Error(response.message || 'Ошибка при отправке сообщения');
                    }
                }

                // Отправляем событие об отправке вопроса
                this.$emit('question-submitted', {
                    question: this.question,
                    userId: window.userId,
                    firstName: this.firstName.trim(),
                    lastName: this.lastName.trim(),
                    contact: this.authMode === 'phone' ? this.phone : this.email,
                });

            } catch (error) {
                console.error('Ошибка отправки вопроса:', error);
                this.questionError = error.message;
            } finally {
                this.isSendingCode = false;
            }
        },

        async sendMessageToChat(data) {
            return new Promise((resolve) => {
                BX.ajax({
                    url: '/api/v1/chat/message.add/',
                    method: 'POST',
                    data: {...data, sessid: BX.bitrix_sessid()},
                    onsuccess: (result) => {
                        try {
                            resolve(JSON.parse(result));
                        } catch {
                            resolve({status: 'error', message: 'Ошибка сервера'});
                        }
                    },
                    onfailure: () => {
                        resolve({status: 'error', message: 'Ошибка соединения'});
                    },
                });
            });
        },

        resetForm() {
            this.removePhoneMask();
            this.firstName = '';
            this.lastName = '';
            this.phone = '';
            this.displayedPhone = '';
            this.email = '';
            this.authCode = '';
            this.question = '';
            this.step = 'contact';
            this.authMode = 'phone';
            this.isCodeVerified = false;
            this.isCodeSent = false;
            this.attemptNumber = 1;
            this.isContactFieldLocked = false;
            this.isCodeFieldLocked = false;
            this.areNameFieldsValid = false;
            this.clearAllTimers();
            this.resetBlockStatus();
        },

        logout() {
            // Сбрасываем глобальные переменные
            window.userVerified = false;
            window.userId = 0;
            window.userName = '';
            window.userLastName = '';
            window.userPhone = '';
            window.userEmail = '';

            // Сбрасываем форму
            this.resetForm();

            // Отправляем событие о выходе
            const logoutEvent = new CustomEvent('chatUserLoggedOut');
            document.dispatchEvent(logoutEvent);

            this.$emit('logout');
        },

        handlePhoneDelete(event) {
            // Если нажата Backspace или Delete и курсор в начале +7
            if ((event.key === 'Backspace' || event.key === 'Delete') &&
                this.displayedPhone &&
                this.displayedPhone.startsWith('+7')) {

                const input = event.target;
                const cursorPos = input.selectionStart;

                // Если пытаются удалить +7 в начале - предотвращаем
                if (cursorPos <= 2) {
                    event.preventDefault();

                }
            }
        },
        handlePhoneFocus(event) {
            const input = event.target;

            // Если поле пустое, ставим +7(
            if (!this.displayedPhone || this.displayedPhone.trim() === '') {
                this.displayedPhone = '+7(';
                input.value = '+7(';

                this.$nextTick(() => {
                    // Ставим курсор после +7(
                    input.setSelectionRange(3, 3);
                });
            }
        },

        formatPhoneForDisplay(phone) {
            if (!phone) return '';

            let digits = phone.toString().replace(/\D/g, '');

            // Приводим к формату +7XXXXXXXXXX
            if (digits.startsWith('8')) {
                digits = '7' + digits.substring(1);
            }
            if (!digits.startsWith('7')) {
                digits = '7' + digits;
            }

            if (digits.length === 11) {
                return `+7(${digits.substring(1, 4)})${digits.substring(4, 7)}-${digits.substring(7, 9)}-${digits.substring(9, 11)}`;
            }

            // Если номер неполный, форматируем что есть
            let formatted = '+7';
            if (digits.length > 1) {
                formatted += '(' + digits.substring(1, 4);
            }
            if (digits.length > 4) {
                formatted += ')' + digits.substring(4, 7);
            }
            if (digits.length > 7) {
                formatted += '-' + digits.substring(7, 9);
            }
            if (digits.length > 9) {
                formatted += '-' + digits.substring(9, 11);
            }

            return formatted;
        },
    },
    mounted() {
        this.checkBlockStatus();

        // Восстанавливаем данные из глобальных переменных если они есть
        if (window.userVerified && window.userId > 0) {
            this.isCodeVerified = true;
            this.step = 'verified';
            this.isContactFieldLocked = true;
            this.isCodeFieldLocked = true;

            if (window.userName) this.firstName = window.userName;
            if (window.userLastName) this.lastName = window.userLastName;
            if (window.userPhone) {
                this.phone = window.userPhone;
                this.displayedPhone = this.formatPhoneForDisplay(window.userPhone);
                this.authMode = 'phone';
            }
            if (window.userEmail) {
                this.email = window.userEmail;
                this.authMode = 'email';
            }
        }

        // Инициализируем валидацию
        this.validateFirstName();
        this.validateLastName();
        if (this.authMode === 'phone') {
            // Форматируем телефон если он есть
            if (this.phone && !this.displayedPhone) {
                this.displayedPhone = this.formatPhoneForDisplay(this.phone);
            }
            this.validatePhone();
        } else {
            this.validateEmail();
        }

    },
    beforeUnmount() {
        this.clearAllTimers();
    },
    template: `
        <div class="verification-wrapper" :class="{ 'compact-mode': compactMode }">
            <div class="verification-main-block">
                <!-- Шаг 1: Контактные данные -->
                <div v-if="step === 'contact'" class="verification-step contact-step">
                    <!-- Поля имени и фамилии -->
                    <div v-if="showNameField" class="name-fields-row">
                        <div class="form-group">
                            <label class="input-label">Имя*</label>
                            <input 
                                type="text" 
                                v-model="firstName"
                                @input="validateFirstName"
                                :class="{ 'has-error': firstNameError }"
                                placeholder="Ваше имя"
                                :disabled="isContactFieldLocked"
                            >
                            <div v-if="firstNameError" class="error-message">{{ firstNameError }}</div>
                        </div>
                        
                        <div v-if="showLastNameField" class="form-group">
                            <label class="input-label">Фамилия*</label>
                            <input 
                                type="text" 
                                v-model="lastName"
                                @input="validateLastName"
                                :class="{ 'has-error': lastNameError }"
                                placeholder="Ваша фамилия"
                                :disabled="isContactFieldLocked"
                            >
                            <div v-if="lastNameError" class="error-message">{{ lastNameError }}</div>
                        </div>
                    </div>
                    
                    <!-- Поле контакта (телефон/email) -->
                    <div class="form-group contact-field">
                        <label class="input-label">{{ authMode === 'phone' ? 'Телефон*' : 'Email*' }}</label>
                        
                        <div class="contact-input-wrapper">
                            <div class="input-row">
                                <div class="input-col">
                                    <input 
                                        v-if="authMode === 'phone'"
                                        id="phone-input"
                                        ref="phoneInput"
                                        :value="displayedPhone"
                                        @input="formatPhoneInput"
                                        @blur="formatPhoneOnBlur"
                                        @keydown="handlePhoneKeydown"
                                        @focus="handlePhoneFocus"
                                        :class="{ 'has-error': phoneError }"
                                        type="tel"
                                        placeholder="+7(999)999-99-99"
                                        :disabled="isContactFieldLocked"
                                    >
                                    
                                    <input 
                                        v-else
                                        v-model="email"
                                        @input="validateEmail"
                                        :class="{ 'has-error': emailError }"
                                        type="email"
                                        placeholder="example@mail.com"
                                        :disabled="isContactFieldLocked"
                                    >
                                </div>
                                
                                <div class="button-col" v-if="!isContactFieldLocked">
                                    <button 
                                        @click="sendVerificationCode"
                                        :disabled="!canSendCode"
                                        class="send-code-button"
                                        :class="{ 'loading': isSendingCode }"
                                    >
                                        <span v-if="isSendingCode">Отправка...</span>
                                        <span v-else>Получить код</span>
                                    </button>
                                </div>
                            </div>
                            <div v-if="userExists !== null && !checkingUser" class="user-exists-status">
                                <span v-if="userExists === true" class="status-exists">
                                    ✓ Пользователь найден, можно отправить код
                                </span>
                                <span v-if="userExists === false" class="status-new">
                                    Новый пользователь
                                </span>
                            </div>
                            <div v-if="phoneError || emailError" class="error-message">
                                {{ authMode === 'phone' ? phoneError : emailError }}
                            </div>
                            <div v-if="userCheckError" class="error-message">{{ userCheckError }}</div>
                            <div v-if="checkingUser" class="loading-message">Проверка пользователя...</div>
                        </div>
                    </div>
                    
                    <!-- Соглашения -->
                    <div v-if="shouldShowConsentsBlock" class="consents-block">
                        <div v-if="showAgreementError" class="error-message">
                            Пожалуйста, подтвердите все обязательные соглашения
                        </div>
                        
                        <div class="consent-item" v-for="consent in serverConsents" :key="consent.key">
                            <label class="consent-label">
                                <input 
                                    type="checkbox" 
                                    v-model="consents[consent.key]"
                                    :required="consent.required"
                                >
                                <span class="consent-text">
                                    <span v-html="consent.text"></span>
                                    <span v-if="consent.required" class="requirement">*</span>
                                </span>
                            </label>
                        </div>
                    </div>
                    
                    <!-- Переключатель между телефоном и email -->
                    <div class="auth-switch-mode">
                        <a @click.prevent="toggleAuthMode" class="switch-method">
                            {{ authMode === 'phone' 
                                ? 'Вход по электронной почте' 
                                : 'Вход по номеру телефона (Россия)' }}
                        </a>
                    </div>
                </div>
                
                <!-- Шаг 2: Подтверждение кода -->
                <div v-if="step === 'code'" class="verification-step code-step">
                    <div class="form-group">
                        <label class="input-label">Код подтверждения</label>
                        
                        <div class="code-input-wrapper">
                            <div class="input-row">
                                <div class="input-col">
                                    <input 
                                        v-model="authCode"
                                        @input="validateCode"
                                        :class="{ 'has-error': codeError }"
                                        type="text"
                                        :placeholder="codePlaceholder"
                                        maxlength="4"
                                        :disabled="isCodeFieldLocked || isCodeVerified"
                                    >
                                </div>
                                
                                <div class="button-col">
                                    <button 
                                        @click="verifyCode"
                                        :disabled="!canVerifyCode"
                                        class="verify-code-button"
                                        :class="{ 'loading': isSendingCode }"
                                    >
                                        <span v-if="isSendingCode">Проверка...</span>
                                        <span v-else>Подтвердить</span>
                                    </button>
                                </div>
                            </div>
                            
                            <div v-if="codeError" class="error-message">{{ codeError }}</div>
                            
                            <div class="timer-section">
                                <div v-if="resendTimeout > 0" class="countdown">
                                    Повторная отправка через {{ countdownFormatted }}
                                </div>
                                
                                <a 
                                    v-if="canResendCode"
                                    @click.prevent="resendVerificationCode"
                                    class="resend-link"
                                >
                                    Отправить код повторно
                                </a>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Информация о контакте -->
                    <div class="contact-info">
                        <p>Код отправлен на:</p>
                        <p class="contact-value">
                            {{ authMode === 'phone' ? formatPhone(phone) : email }}
                        </p>
                        <a 
                            v-if="!isCodeVerified"
                            @click.prevent="step = 'contact'; isContactFieldLocked = false;"
                            class="change-contact-link"
                        >
                            Изменить контакт
                        </a>
                    </div>
                </div>
                
                <!-- Шаг 3: Верификация завершена -->
                <div v-if="step === 'verified'" class="verification-step verified-step">
                    <div class="verification-success">
                        <div class="success-icon">✓</div>
                        <h6>Верификация завершена!</h6>
                        <p>Теперь у вас есть доступ ко всем функциям чата</p>
                        
                        <div class="user-info">
                            <div class="info-item">
                                <span class="label">Имя:</span>
                                <span class="value">{{ firstName }} {{ lastName }}</span>
                            </div>
                            <div v-if="authMode === 'phone'" class="info-item">
                                <span class="label">Телефон:</span>
                                <span class="value">{{ formatPhone(phone) }}</span>
                            </div>
                            <div v-else class="info-item">
                                <span class="label">Email:</span>
                                <span class="value">{{ email }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Поле вопроса (если включено) -->
                    <div v-if="showQuestionField" class="question-field">
                        <div class="form-group">
                            <label class="input-label">Ваш вопрос</label>
                            <textarea 
                                v-model="question"
                                @input="validateQuestion"
                                :class="{ 'has-error': questionError }"
                                rows="3"
                                placeholder="Введите ваш вопрос..."
                            ></textarea>
                            <div v-if="questionError" class="error-message">{{ questionError }}</div>
                        </div>
                        
                        <button 
                            @click="submitQuestion"
                            :disabled="!canSubmitQuestion"
                            class="submit-question-button"
                            :class="{ 'loading': isSendingCode }"
                        >
                            <span v-if="isSendingCode">Отправка...</span>
                            <span v-else>Отправить вопрос</span>
                        </button>
                    </div>
                    
                    <!-- Кнопка выхода -->
                    <div class="logout-section">
                        <button @click="logout" class="logout-button">
                            Выйти
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `,
};